import { useEffect, useMemo, useState } from "react";
import { T, fontDisplay, fontMono } from "../theme";
import { fmtDay, fmtMoney, isoDate, todayStr } from "../engine/dates";
import { applyAdjustments, freezeTxn, scenarioEffect, scenarioWeeks } from "../engine/scenario";
import { createScenario, deleteScenario, loadScenarios, updateScenario } from "../lib/data";

/**
 * Scenarios: a copy of the forecast, changed, with the argument written on it.
 *
 * A scenario is frozen at creation. The point of sending someone a link is that
 * it keeps saying what it said when you sent it — a scenario that tracked live
 * data would quietly become a different case, and the commentary written
 * against it would stop being true.
 *
 * Adjustments are kept as a list rather than baked into the copy, so what was
 * changed stays visible beside what was there. Showing someone a scenario is an
 * argument, and an argument you cannot inspect is just an assertion.
 */
export default function ScenariosView({ forecast, weeks, openingBalance, openingDate, overdraftLimit }) {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    name: "",
    commentary: "",
    fromDate: todayStr(),
    weeks: 26,
    requireCode: true,
  });

  const refresh = async () => {
    try {
      setList(await loadScenarios());
    } catch (e) {
      setError(e.message || String(e));
    }
  };
  useEffect(() => {
    refresh();
  }, []);

  const create = async () => {
    setBusy("new");
    setError(null);
    try {
      const from = draft.fromDate;
      const txns = (forecast?.txns || [])
        .filter((t) => !t.isHistory && isoDate(t.date) >= from)
        .map(freezeTxn);
      const opening = weeks?.find((w) => isoDate(w.start) >= from)?.startBal ?? openingBalance;
      const made = await createScenario({
        name: draft.name.trim() || `Forecast as at ${fmtDay(from)}`,
        commentary: draft.commentary,
        fromDate: from,
        weeks: Number(draft.weeks) || 26,
        txns,
        openingBalance: Number(opening) || 0,
        openingDate,
        overdraftLimit,
        requireCode: draft.requireCode,
      });
      setCreating(false);
      setDraft({ name: "", commentary: "", fromDate: todayStr(), weeks: 26, requireCode: true });
      await refresh();
      setOpenId(made.id);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  const field = (extra = {}) => ({
    fontFamily: "inherit",
    fontSize: 12,
    padding: "6px 8px",
    border: `1px solid ${T.border}`,
    borderRadius: 6,
    background: "transparent",
    ...extra,
  });

  return (
    <div>
      <h2 style={{ fontFamily: fontDisplay, fontSize: 22, marginBottom: 4 }}>Scenarios</h2>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 760 }}>
        A copy of the forecast taken now, changed however the argument needs, and shared on its own
        link. The copy is frozen: what you send on Tuesday still says what it said on Tuesday,
        however far the live forecast has moved since.
      </p>

      {error && <div style={{ color: T.cost, fontSize: 12, margin: "8px 0" }}>{error}</div>}

      <button
        onClick={() => setCreating((v) => !v)}
        style={{
          padding: "8px 14px",
          borderRadius: 6,
          border: "none",
          background: T.accent,
          color: "#fff",
          fontSize: 12,
          cursor: "pointer",
          fontFamily: "inherit",
          margin: "12px 0",
        }}
      >
        {creating ? "Cancel" : "Take a copy of the forecast"}
      </button>

      {creating && (
        <div style={{ background: T.panel, borderRadius: 10, padding: 16, marginBottom: 16, maxWidth: 760 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: 11, color: T.muted }}>
              What is this showing?
              <br />
              <input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Base case, October board pack"
                style={field({ width: 300, marginTop: 2 })}
              />
            </label>
            <label style={{ fontSize: 11, color: T.muted }}>
              From
              <br />
              <input
                type="date"
                value={draft.fromDate}
                onChange={(e) => setDraft({ ...draft, fromDate: e.target.value })}
                style={field({ marginTop: 2, fontFamily: fontMono })}
              />
            </label>
            <label style={{ fontSize: 11, color: T.muted }}>
              Weeks
              <br />
              <input
                type="number"
                min="4"
                max="52"
                value={draft.weeks}
                onChange={(e) => setDraft({ ...draft, weeks: e.target.value })}
                style={field({ width: 70, marginTop: 2, fontFamily: fontMono })}
              />
            </label>
          </div>
          <label style={{ fontSize: 11, color: T.muted, display: "block", marginTop: 10 }}>
            Commentary — what this scenario demonstrates
            <textarea
              value={draft.commentary}
              onChange={(e) => setDraft({ ...draft, commentary: e.target.value })}
              rows={3}
              placeholder="What the reader should take from this, and what has been assumed."
              style={field({ width: "100%", marginTop: 4, resize: "vertical" })}
            />
          </label>
          <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
            <input
              type="checkbox"
              checked={draft.requireCode}
              onChange={(e) => setDraft({ ...draft, requireCode: e.target.checked })}
            />
            ask for the access code before showing it
          </label>
          <button
            onClick={create}
            disabled={busy === "new"}
            style={{
              marginTop: 12,
              padding: "8px 16px",
              borderRadius: 6,
              border: "none",
              background: T.accent,
              color: "#fff",
              fontSize: 12,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {busy === "new" ? "Copying…" : "Create scenario"}
          </button>
        </div>
      )}

      {list.map((sc) => (
        <Scenario
          key={sc.id}
          sc={sc}
          open={openId === sc.id}
          onToggle={() => setOpenId(openId === sc.id ? null : sc.id)}
          onChanged={refresh}
        />
      ))}

      {!list.length && !creating && (
        <div style={{ background: T.panel, borderRadius: 10, padding: 14, fontSize: 12, color: T.muted, maxWidth: 760 }}>
          No scenarios yet.
        </div>
      )}
    </div>
  );
}

function Scenario({ sc, open, onToggle, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copied, setCopied] = useState(false);
  const [commentary, setCommentary] = useState(sc.commentary || "");
  const [adds, setAdds] = useState({ label: "", amount: "", date: todayStr(), note: "" });

  const link = `${window.location.origin}/s/${sc.token}`;
  const opts = {
    fromDate: sc.from_date,
    weeks: sc.weeks,
    openingBalance: Number(sc.opening_balance) || 0,
  };
  const adjusted = useMemo(
    () => applyAdjustments(sc.base || [], sc.adjustments || []),
    [sc.base, sc.adjustments]
  );
  const effect = useMemo(
    () => scenarioEffect(sc.base || [], adjusted, opts),
    [sc.base, adjusted, sc.from_date, sc.weeks, sc.opening_balance]
  );

  const save = async (patch) => {
    setBusy(true);
    try {
      await updateScenario(sc.id, patch);
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  const setAdjust = (next) => save({ adjustments: next });
  const adjustments = sc.adjustments || [];
  const droppedKeys = new Set(adjustments.filter((a) => a.op === "drop").map((a) => a.key));

  // Only what is worth deciding about: the largest lines, either way.
  const notable = useMemo(
    () =>
      [...(sc.base || [])]
        .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
        .slice(0, 40),
    [sc.base]
  );

  const cell = { padding: "6px 10px", fontSize: 12 };

  return (
    <div style={{ background: T.panel, borderRadius: 10, marginBottom: 10, maxWidth: 940 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px" }}>
        <button
          onClick={onToggle}
          style={{ border: "none", background: "none", cursor: "pointer", textAlign: "left", font: "inherit", flex: 1 }}
        >
          <div style={{ fontSize: 14, fontWeight: 600 }}>{sc.name}</div>
          <div style={{ fontSize: 11, color: T.muted }}>
            {fmtDay(sc.from_date)} · {sc.weeks} weeks · taken {fmtDay(sc.created_at)} ·{" "}
            {(sc.adjustments || []).length} change{(sc.adjustments || []).length === 1 ? "" : "s"}
            {sc.require_code ? " · code required" : " · open link"}
          </div>
        </button>
        <div style={{ textAlign: "right", fontFamily: fontMono, fontSize: 12 }}>
          <div style={{ color: effect.lowestAfter < 0 ? T.cost : T.ink }}>
            {fmtMoney(effect.lowestAfter)}
          </div>
          <div style={{ fontSize: 10, color: T.muted }}>lowest</div>
        </div>
        <button
          onClick={() => {
            navigator.clipboard?.writeText(link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          style={{
            border: `1px solid ${T.border}`,
            background: "transparent",
            borderRadius: 6,
            padding: "6px 11px",
            fontSize: 11,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {copied ? "copied" : "copy link"}
        </button>
      </div>

      {open && (
        <div style={{ padding: "0 16px 16px" }}>
          <div style={{ fontSize: 11, color: T.muted, marginBottom: 10, fontFamily: fontMono }}>
            {link}
          </div>

          <label style={{ fontSize: 11, color: T.muted, display: "block", marginBottom: 12 }}>
            Commentary
            <textarea
              value={commentary}
              onChange={(e) => setCommentary(e.target.value)}
              onBlur={() => commentary !== (sc.commentary || "") && save({ commentary })}
              rows={3}
              style={{
                width: "100%",
                marginTop: 4,
                fontFamily: "inherit",
                fontSize: 12,
                padding: 8,
                border: `1px solid ${T.border}`,
                borderRadius: 6,
                resize: "vertical",
              }}
            />
          </label>

          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11, marginBottom: 12 }}>
            <Delta label="Lowest point" before={effect.lowestBefore} after={effect.lowestAfter} />
            <Delta label="Closes at" before={effect.endBefore} after={effect.endAfter} />
            <Delta label="Money in" before={effect.inBefore} after={effect.inAfter} />
            <Delta label="Money out" before={effect.outBefore} after={effect.outAfter} />
          </div>

          <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginBottom: 6 }}>
            Add something speculative
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 14 }}>
            <input
              value={adds.label}
              onChange={(e) => setAdds({ ...adds, label: e.target.value })}
              placeholder="New retainer — Client X"
              style={{ fontSize: 12, padding: "6px 8px", width: 220 }}
            />
            <input
              value={adds.amount}
              onChange={(e) => setAdds({ ...adds, amount: e.target.value })}
              placeholder="8000 or -8000"
              style={{ fontFamily: fontMono, fontSize: 12, padding: "6px 8px", width: 120 }}
            />
            <input
              type="date"
              value={adds.date}
              onChange={(e) => setAdds({ ...adds, date: e.target.value })}
              style={{ fontFamily: fontMono, fontSize: 12, padding: "6px 8px" }}
            />
            <button
              disabled={busy || !adds.label.trim() || !Number(adds.amount)}
              onClick={() => {
                setAdjust([
                  ...adjustments,
                  {
                    op: "add",
                    id: `${Date.now()}`,
                    label: adds.label.trim(),
                    amount: Number(adds.amount),
                    date: adds.date,
                    category: Number(adds.amount) >= 0 ? "income" : "other",
                  },
                ]);
                setAdds({ label: "", amount: "", date: todayStr(), note: "" });
              }}
              style={{
                padding: "6px 12px",
                borderRadius: 6,
                border: "none",
                background: adds.label.trim() && Number(adds.amount) ? T.accent : T.border,
                color: adds.label.trim() && Number(adds.amount) ? "#fff" : T.muted,
                fontSize: 11,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Add
            </button>
          </div>

          {adjustments.filter((a) => a.op === "add").length > 0 && (
            <div style={{ marginBottom: 14 }}>
              {adjustments
                .filter((a) => a.op === "add")
                .map((a) => (
                  <div key={a.id} style={{ fontSize: 11, display: "flex", gap: 8, padding: "3px 0" }}>
                    <span style={{ color: T.pipeline, fontWeight: 700 }}>SPECULATIVE</span>
                    <span>{a.label}</span>
                    <span style={{ fontFamily: fontMono }}>{fmtMoney(a.amount)}</span>
                    <span style={{ color: T.muted }}>{fmtDay(a.date)}</span>
                    <button
                      onClick={() => setAdjust(adjustments.filter((x) => x !== a))}
                      style={{ border: "none", background: "none", color: T.muted, cursor: "pointer", marginLeft: "auto" }}
                    >
                      ×
                    </button>
                  </div>
                ))}
            </div>
          )}

          <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginBottom: 6 }}>
            Take things out — the forty largest lines in this copy
          </div>
          <div style={{ maxHeight: 320, overflowY: "auto", border: `1px solid ${T.borderSoft}`, borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {notable.map((t) => {
                  const gone = droppedKeys.has(t.key);
                  return (
                    <tr
                      key={t.key}
                      style={{
                        borderTop: `1px solid ${T.borderSoft}`,
                        opacity: gone ? 0.45 : 1,
                        background: gone ? "#FBEFE2" : undefined,
                      }}
                    >
                      <td style={{ ...cell, width: 30 }}>
                        <input
                          type="checkbox"
                          checked={!gone}
                          disabled={busy}
                          title="In this scenario"
                          onChange={(e) =>
                            setAdjust(
                              e.target.checked
                                ? adjustments.filter((a) => !(a.op === "drop" && a.key === t.key))
                                : [...adjustments, { op: "drop", key: t.key }]
                            )
                          }
                        />
                      </td>
                      <td style={cell}>
                        {t.label}
                        <span style={{ fontSize: 10, color: T.muted, marginLeft: 6 }}>
                          {t.source?.docNo || t.category}
                          {t.isPipeline ? " · quoted" : ""}
                        </span>
                      </td>
                      <td style={{ ...cell, color: T.muted, fontSize: 11 }}>{fmtDay(t.date)}</td>
                      <td
                        style={{
                          ...cell,
                          textAlign: "right",
                          fontFamily: fontMono,
                          color: t.amount >= 0 ? T.income : T.cost,
                          textDecoration: gone ? "line-through" : "none",
                        }}
                      >
                        {fmtMoney(t.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
            <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="checkbox"
                checked={sc.require_code}
                onChange={(e) => save({ require_code: e.target.checked })}
              />
              ask for the access code
            </label>
            {confirmDelete ? (
              <>
                <button
                  onClick={async () => {
                    await deleteScenario(sc.id);
                    await onChanged();
                  }}
                  style={{ marginLeft: "auto", border: "none", background: T.cost, color: "#fff", borderRadius: 6, padding: "5px 11px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
                >
                  Delete — the link stops working
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  style={{ border: "none", background: "none", color: T.muted, fontSize: 11, cursor: "pointer" }}
                >
                  no
                </button>
              </>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                style={{ marginLeft: "auto", border: `1px solid ${T.border}`, background: "transparent", borderRadius: 6, padding: "5px 11px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", color: T.muted }}
              >
                delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Delta({ label, before, after }) {
  const moved = after - before;
  return (
    <div>
      <div style={{ color: T.muted, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6 }}>
        {label}
      </div>
      <div style={{ fontFamily: fontMono }}>
        <span style={{ color: T.muted }}>{fmtMoney(before)}</span> →{" "}
        <strong>{fmtMoney(after)}</strong>
      </div>
      {Math.abs(moved) > 1 && (
        <div style={{ fontSize: 10, color: moved < 0 ? T.cost : T.income }}>
          {moved > 0 ? "+" : ""}
          {fmtMoney(moved)}
        </div>
      )}
    </div>
  );
}
